import React from "react";
import { Check, Crown, Sparkles, Zap, ChevronDown, Quote } from "lucide-react";

/**
 * Marketing sections for the public landing page.
 * Pricing mirrors /app/backend/billing.py (TIERS + PRICES) — keep in sync if either changes.
 * Testimonials are intentional placeholders; swap in real customer quotes as they come in.
 */

// ─── Pricing ──────────────────────────────────────────────────────────────────

const TIERS = [
  {
    key: "free",
    name: "Free",
    icon: Sparkles,
    tagline: "Try Vendor Vault at one show.",
    monthly: 0,
    yearly: 0,
    features: [
      "Up to 20 products",
      "1 active event",
      "Solo (no team)",
      "Barcodes, scanner, sales tracking",
      "Webcam barcode lookup",
      "PWA — install on phone",
    ],
    cta: "Start free",
    highlight: false,
  },
  {
    key: "pro",
    name: "Pro",
    icon: Zap,
    tagline: "For makers running multiple shows.",
    monthly: 15,
    yearly: 144, // $12 / mo equivalent
    features: [
      "Unlimited products & events",
      "5 team members",
      "CSV import + export",
      "Per-seller payouts",
      "Shopify & Square integrations",
      "By-day & date-range analytics",
      "Offline write queue with auto-sync",
    ],
    cta: "Start Pro",
    highlight: true,
    badge: "Most popular",
  },
  {
    key: "business",
    name: "Business",
    icon: Crown,
    tagline: "For full booths and growing teams.",
    monthly: 39,
    yearly: 374,
    features: [
      "Everything in Pro",
      "Unlimited team members",
      "Priority email support",
      "Bulk Shopify catalog sync",
      "Square POS deep-link checkout",
      "Per-event sale-by-sale ledger",
    ],
    cta: "Start Business",
    highlight: false,
  },
];

export function PricingSection({ onPick }) {
  const [yearly, setYearly] = React.useState(false);

  return (
    <section id="pricing" className="bg-white border-t-2 border-black">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 lg:mb-14">
          <div>
            <span className="brutal-badge mb-3">Pricing</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mt-3">
              Pay for what you<br/>actually pack.
            </h2>
            <p className="mt-4 font-mono text-sm text-[#4A4A4A] max-w-md leading-relaxed">
              14-day Pro trial on every new account. No credit card required to start. Cancel anytime from <code>/billing</code>.
            </p>
          </div>

          {/* Monthly / Yearly toggle */}
          <div className="inline-flex border-2 border-black bg-white" data-testid="pricing-toggle">
            <button
              type="button"
              onClick={() => setYearly(false)}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border-r-2 border-black ${!yearly ? "bg-[#111] text-white" : "bg-white hover:bg-[#F4F2EC]"}`}
              data-testid="pricing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 ${yearly ? "bg-[#111] text-white" : "bg-white hover:bg-[#F4F2EC]"}`}
              data-testid="pricing-toggle-yearly"
            >
              Yearly
              <span className={`brutal-badge !py-0 !px-1 !text-[9px] ${yearly ? "!bg-[#C2410C] !text-white" : ""}`}>−20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {TIERS.map((t) => (
            <PricingCard
              key={t.key}
              tier={t}
              yearly={yearly}
              onPick={() => onPick?.(t.key)}
            />
          ))}
        </div>

        <p className="mt-10 text-center font-mono text-xs text-[#4A4A4A]">
          Prices in USD. Yearly billed once a year — works out to <strong>$12/mo</strong> on Pro,
          <strong> $31/mo</strong> on Business. Taxes added at checkout where applicable.
        </p>
      </div>
    </section>
  );
}

function PricingCard({ tier, yearly, onPick }) {
  const Icon = tier.icon;
  const amount = yearly ? tier.yearly : tier.monthly;
  const period = tier.monthly === 0 ? "" : (yearly ? "/yr" : "/mo");
  const isFree = tier.key === "free";
  const monthlyEquiv = yearly && !isFree ? `≈ $${Math.round(tier.yearly / 12)}/mo` : null;

  return (
    <div
      className={`brutal-card p-6 lg:p-8 relative ${tier.highlight ? "!bg-[#111] !text-white -translate-y-2 corner-marks" : "bg-white"}`}
      data-testid={`pricing-card-${tier.key}`}
    >
      {tier.badge && (
        <span className="absolute -top-3 left-6 brutal-badge !bg-[#C2410C] !text-white !py-1">
          {tier.badge}
        </span>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 border-2 border-black flex items-center justify-center ${tier.highlight ? "bg-[#C2410C]" : "bg-[#F4F2EC]"}`}>
          <Icon size={20} className={tier.highlight ? "text-white" : ""}/>
        </div>
        <div className="font-heading text-2xl font-black uppercase">{tier.name}</div>
      </div>
      <p className={`mt-3 font-mono text-xs leading-relaxed ${tier.highlight ? "opacity-80" : "text-[#4A4A4A]"}`}>{tier.tagline}</p>

      <div className="mt-6">
        <div className="font-heading text-5xl font-black tracking-tight">
          ${amount}
          <span className={`font-mono text-sm font-normal ml-1 ${tier.highlight ? "opacity-70" : "text-[#4A4A4A]"}`}>{period}</span>
        </div>
        {monthlyEquiv && (
          <div className={`font-mono text-[11px] mt-1 ${tier.highlight ? "opacity-70" : "text-[#4A4A4A]"}`}>{monthlyEquiv}</div>
        )}
      </div>

      <ul className="mt-6 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 font-mono text-xs leading-snug">
            <Check size={14} className={`mt-[2px] flex-shrink-0 ${tier.highlight ? "text-[#C2410C]" : "text-[#4A7C59]"}`}/>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onPick}
        className={`brutal-btn w-full mt-8 ${tier.highlight ? "" : "brutal-btn-secondary"}`}
        data-testid={`pricing-cta-${tier.key}`}
      >
        {tier.cta} →
      </button>
    </div>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────

// NOTE: Placeholder copy. Swap with real customer quotes as they come in.
const TESTIMONIALS = [
  {
    quote:
      "Setup at the next con took 10 minutes instead of an hour. Barcode tags printed at home, scan, sell, done. The booth feels like a real shop now.",
    name: "Maya O.",
    role: "Ceramics & enamel pins · Maker in Brooklyn",
  },
  {
    quote:
      "I run six shows a season and used to lose track of which shirts moved where. The per-event report tells me what to restock before the next gate even opens.",
    name: "Derek L.",
    role: "Apparel · Tattoo conventions",
  },
  {
    quote:
      "Hiring my partner as Staff for the weekend with a single invite is the killer feature. No more 'wait, what's the price on that?'",
    name: "Aiko T.",
    role: "Sticker artist · Artist alleys",
  },
];

export function TestimonialsSection() {
  return (
    <section id="testimonials" className="bg-[#F4F2EC] border-t-2 border-black">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="brutal-badge mb-3">From the makers</span>
          <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mt-3">
            What vendors are saying.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {TESTIMONIALS.map((t, idx) => (
            <div key={idx} className="brutal-card bg-white p-6 lg:p-7" data-testid={`testimonial-${idx}`}>
              <Quote size={26} className="text-[#C2410C]" strokeWidth={2.5}/>
              <p className="mt-4 font-mono text-sm leading-relaxed text-black">
                "{t.quote}"
              </p>
              <div className="mt-6 pt-5 border-t-2 border-black">
                <div className="font-heading text-base font-black uppercase">{t.name}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A] mt-1">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const FAQ = [
  {
    q: "Is there really a free plan?",
    a: "Yes — up to 20 products and 1 active event, forever. Solo only, but it covers a single weekend show end-to-end. Every new account also gets a 14-day Pro trial automatically so you can try the integrations before deciding.",
  },
  {
    q: "How do barcodes work — do I need a special printer?",
    a: "No. Every variant gets an auto-generated SKU and a Code128 barcode. You print them on a standard inkjet/laser printer on regular sticker paper or shipping-tag stock — there's a built-in 'Print Tag' layout sized for both.",
  },
  {
    q: "Can I scan with my phone?",
    a: "Yes — the scanner uses your phone's webcam (no app install). On a busy show day a USB / Bluetooth barcode scanner works too; it just types into the same lookup field.",
  },
  {
    q: "Does it work offline?",
    a: "Yes. Vendor Vault is a PWA — you can install it on your phone home screen. Read access (products, scanner lookup, event detail) keeps working offline from a local cache, and any sales or stock adjustments you make get queued and auto-sync the next time you're online.",
  },
  {
    q: "Can I sync with Shopify?",
    a: "Yes — on Pro and Business. One-click OAuth into your Shopify store, then push your Vendor Vault catalog (products + variants + inventory + categories as Collections) to Shopify with a single button. Resume-on-failure is built in.",
  },
  {
    q: "What about taking card payments?",
    a: "Square POS integration on Pro and Business — Vendor Vault deep-links into the native Square app on your phone with the cart total pre-filled, your Square Reader (or Tap-to-Pay) handles the card, and the payment + Square order ID come back to Vendor Vault automatically.",
  },
  {
    q: "Can I have my partner help at the booth?",
    a: "Yes. Pro = up to 5 team members, Business = unlimited. Invite them by email; they get a Staff role with full scanner / sales / stock-adjust access but no destructive permissions (delete, billing, team management stay admin-only).",
  },
  {
    q: "Can I export my data?",
    a: "Always. CSV export of all products + variants is one click, free tier included. You own your data — we won't lock it up.",
  },
  {
    q: "What if I want to cancel?",
    a: "Cancel from /billing with one click; your subscription stays active until the end of the period you paid for. Your data stays accessible on Free (within the 20-product / 1-event limits) and can be exported at any time.",
  },
  {
    q: "I run a much bigger operation — do you do custom?",
    a: "Hit us at support@vendorvaultonline.com and let's talk. Business is the published top tier today, but if you've got specific needs we'll figure it out.",
  },
];

export function FAQSection() {
  const [openIdx, setOpenIdx] = React.useState(0);

  return (
    <section id="faq" className="bg-white border-t-2 border-black">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="mb-10 lg:mb-14">
          <span className="brutal-badge mb-3">Questions</span>
          <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mt-3">
            FAQ
          </h2>
          <p className="mt-4 font-mono text-sm text-[#4A4A4A] max-w-xl leading-relaxed">
            Couldn't find what you're looking for? Email <a href="mailto:support@vendorvaultonline.com" className="text-[#C2410C] underline">support@vendorvaultonline.com</a> — we're a small team and we read everything.
          </p>
        </div>

        <div className="space-y-3" data-testid="faq-list">
          {FAQ.map((item, idx) => {
            const open = idx === openIdx;
            return (
              <div key={idx} className={`brutal-card-sm bg-white overflow-hidden ${open ? "shadow-[3px_3px_0_0_#111]" : ""}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full px-5 py-4 flex items-start justify-between gap-4 text-left hover:bg-[#F4F2EC]"
                  data-testid={`faq-q-${idx}`}
                  aria-expanded={open}
                >
                  <span className="font-heading text-base lg:text-lg font-black uppercase leading-snug">{item.q}</span>
                  <ChevronDown
                    size={20}
                    className={`flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-5 -mt-1 font-mono text-sm leading-relaxed text-[#4A4A4A]" data-testid={`faq-a-${idx}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
